import { Link, useSearchParams } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties } from 'react';
import type { Item } from '@mas-flow/types';

function CardInner({ item }: { item: Item }) {
  return (
    <>
      <div className="line-clamp-2 text-sm font-medium text-slate-900">{item.title}</div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
        {item.wsjf != null ? (
          <span className="rounded bg-brand-wash px-1.5 py-0.5 text-brand-dark">WSJF {item.wsjf.toFixed(1)}</span>
        ) : (
          <span className="rounded bg-slate-100 px-1.5 py-0.5">unscored</span>
        )}
        {item.isAnchor && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">⚓ anchor</span>}
      </div>
    </>
  );
}

/** Presentational copy of the card rendered inside the DragOverlay while dragging. */
export function ItemCardOverlay({ item }: { item: Item }) {
  return (
    <div className="cursor-grabbing rounded border border-slate-300 bg-white p-3 shadow-lg">
      <CardInner item={item} />
    </div>
  );
}

export function ItemCard({ item }: { item: Item }) {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set('item', item.id);

  const isSelected = params.get('item') === item.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  // While dragging, the DragOverlay is the moving visual — the in-list card
  // stays put in its (possibly reassigned) slot as a dimmed placeholder.
  const style: CSSProperties = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Link
        to={{ search: `?${next.toString()}` }}
        onClick={(e) => { if (isDragging) e.preventDefault(); }}
        aria-current={isSelected ? 'true' : undefined}
        className={`block rounded border bg-white p-3 shadow-sm ${
          isSelected
            ? 'border-brand ring-2 ring-brand-tint'
            : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <CardInner item={item} />
      </Link>
    </div>
  );
}
